import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { POS_SHORTCUTS } from '../../data/pos-shortcuts';

export interface GuideSection {
  id: string;
  title: string;
  icon: string;
  content: GuideContent[];
}

export interface GuideContent {
  type: 'text' | 'list' | 'steps' | 'image' | 'warning' | 'tip';
  title?: string;
  content?: string;
  items?: string[];
  imagePath?: string;
  imageAlt?: string;
}

@Component({
  selector: 'app-user-guide-dialog',
  standalone: false,
  templateUrl: './user-guide-dialog.component.html',
  styleUrl: './user-guide-dialog.component.scss',
})
export class UserGuideDialogComponent implements OnInit {
  selectedSection: GuideSection | null = null;
  searchQuery = '';
  filteredSections: GuideSection[] = [];

  sections: GuideSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'play_circle',
      content: [
        {
          type: 'text',
          content:
            'Welcome to the Optwaves POS System. This guide explains how the platform actually works today — the menus you see, the workflows behind each module, and the shortcuts that speed things up.',
        },
        {
          type: 'steps',
          title: 'Sign in and get oriented',
          items: [
            'Sign in with the email and password provided by your administrator on the login screen.',
            'Forgot your password? Use the "Forgot password" link on the login screen to receive a reset email.',
            'If your account has access to more than one location, pick the correct site from the site switcher in the top bar.',
            'Use the left sidebar to navigate. Press Ctrl + K (or click the search box) to jump to any item quickly.',
            'Open the user menu in the top-right to edit your profile or sign out.',
          ],
        },
        {
          type: 'tip',
          title: 'You only see what you have access to',
          content:
            'Menus, buttons and dashboard panels are permission-based. If a feature in this guide is not visible to you, your role does not include that permission — ask your administrator.',
        },
      ],
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: 'dashboard',
      content: [
        {
          type: 'text',
          content:
            'The Dashboard gives you an at-a-glance view of business performance for the active site.',
        },
        {
          type: 'list',
          title: 'What you can see',
          items: [
            'KPI tiles: total sales, transaction count, average order value, active customers, inventory value and total products.',
            'A sales trend chart over the selected period.',
            'Payment-method breakdown and inventory status panels.',
            'Recent transactions, low-stock alerts, top products and top customers.',
          ],
        },
        {
          type: 'steps',
          title: 'Change the period',
          items: [
            'Use the period selector to switch between Today, Week, Month and Year.',
            'All tiles and charts refresh to reflect the chosen range and the active site.',
          ],
        },
        {
          type: 'tip',
          title: 'Permission-aware layout',
          content:
            'Individual tiles, charts and tables only appear if your role has the matching report permission, so two users may see different dashboards.',
        },
      ],
    },
    {
      id: 'pos',
      title: 'Point of Sale',
      icon: 'point_of_sale',
      content: [
        {
          type: 'text',
          content:
            'POS Checkout is your main tool for ringing up sales. It is built for speed, with function-key shortcuts for every common action.',
        },
        {
          type: 'steps',
          title: 'Process a sale',
          items: [
            'Open POS Checkout from the sidebar.',
            'Find products by scanning a barcode or typing in the search box (F1).',
            'Click a product to add it to the cart; adjust the quantity with the quantity control (F6).',
            'Optionally attach a customer — search for an existing one (F2) or create a new one on the fly (F3) to apply loyalty pricing.',
            'Apply an order discount if needed (F7) as a percentage or fixed amount.',
            'Open the payment dialog (F10) and choose a payment method.',
            'Complete the sale (F12), then print or share the receipt.',
          ],
        },
        {
          type: 'list',
          title: 'Payment methods',
          items: [
            'Cash — with quick-amount buttons (500, 1000, 2000, 5000, 10000) and automatic change calculation.',
            'Card — Visa, Mastercard, Amex or Other, capturing the last 4 digits and a reference.',
            'Mobile — eZ Cash, mCash, Genie, FriMi or Other, with the mobile number and reference.',
            'Split payment — combine cash, card and mobile amounts in a single transaction.',
          ],
        },
        {
          type: 'list',
          title: 'Speed features',
          items: [
            'Hold a sale (F4) to park the current cart, and recall it later (F5).',
            'Add an order note (F9) for special instructions.',
            'Print a quote for the current cart (F11) before taking payment.',
            'Loyalty-tier pricing is detected automatically and shown as a badge when a tiered customer is attached.',
          ],
        },
        {
          type: 'steps',
          title: 'Review past sales (Transactions)',
          items: [
            'Open Transactions from the sidebar (under Sales & CRM).',
            'Filter by date range, payment method (cash/card/mobile/split) and status (completed/refunded/cancelled).',
            'Open any transaction to view or reprint its receipt.',
            'Export the filtered list to CSV.',
          ],
        },
        {
          type: 'warning',
          title: 'Check your active site',
          content:
            'Every transaction is recorded against the active site. Confirm the correct site is selected in the top bar before you start selling.',
        },
      ],
    },
    {
      id: 'products',
      title: 'Product Management',
      icon: 'inventory_2',
      content: [
        {
          type: 'text',
          content:
            'Manage your catalog from Products, with Categories and Brands available as their own pages under the Catalog menu.',
        },
        {
          type: 'steps',
          title: 'Add a product',
          items: [
            'Open Products from the sidebar and click "Add Product".',
            'Enter the name, SKU, description and barcode.',
            'Set the selling price and cost, and choose a category and brand.',
            'Toggle stock tracking on if inventory should be enforced for this product.',
            'Set the status to active, then save.',
          ],
        },
        {
          type: 'list',
          title: 'Working with the catalog',
          items: [
            'Filter the product list by search term, category, brand, price range and in-stock status.',
            'Select products to print barcode labels.',
            'Manage Categories at Catalog > Categories (create / edit / delete).',
            'Manage Brands at Catalog > Brands (create / edit / delete).',
          ],
        },
        {
          type: 'tip',
          title: 'Naming matters',
          content:
            'Use clear, consistent names and SKUs — the POS search and reports group and match on these fields.',
        },
      ],
    },
    {
      id: 'inventory',
      title: 'Inventory Management',
      icon: 'warehouse',
      content: [
        {
          type: 'text',
          content:
            'Inventory covers the full procurement chain and stock control, each available as its own page under the Inventory menu.',
        },
        {
          type: 'steps',
          title: 'Procurement workflow (PO → GRN → Invoice → Payment)',
          items: [
            'Maintain your Suppliers (name, contact person, phone, email, address).',
            'Raise a Purchase Order to a supplier with the products, quantities and costs.',
            'When goods arrive, record a Goods Received Note (GRN) to verify and book the quantities received.',
            'Capture the supplier Purchase Invoice and record payments against it; track its payment status.',
          ],
        },
        {
          type: 'list',
          title: 'Stock control',
          items: [
            'Stock Alerts — review low-stock warnings against per-product thresholds.',
            'Stock Transfers — move stock between sites with a send/receive (and cancel) workflow.',
            'Stock Adjustments — correct stock for damage, loss or discrepancies, with a reason recorded.',
          ],
        },
        {
          type: 'warning',
          title: 'Confirm both ends of a transfer',
          content:
            'A stock transfer only settles once the receiving site confirms it. Always verify the quantities received to avoid discrepancies.',
        },
      ],
    },
    {
      id: 'customers',
      title: 'Customer Management',
      icon: 'people',
      content: [
        {
          type: 'text',
          content:
            'Build your customer base and reward repeat business with the built-in loyalty program.',
        },
        {
          type: 'steps',
          title: 'Add a customer',
          items: [
            'Open Customers from the sidebar and click "Add Customer".',
            'Enter the name, email, phone and address.',
            'Assign a loyalty tier if applicable.',
            'Save — the customer can then be attached to sales at the POS.',
          ],
        },
        {
          type: 'list',
          title: 'Loyalty & insights',
          items: [
            'Loyalty tiers: Bronze, Silver, Gold and Platinum.',
            'Each customer tracks loyalty points, total spend and last purchase date.',
            'Tiered customers automatically receive loyalty pricing at the POS.',
            'Filter the directory by loyalty tier, spend range, email status and active status.',
          ],
        },
        {
          type: 'tip',
          title: 'Attach customers at checkout',
          content:
            'Encourage staff to attach a customer to each sale (F2/F3 at the POS) so points, spend and purchase history stay accurate.',
        },
      ],
    },
    {
      id: 'reports',
      title: 'Reports & Analytics',
      icon: 'assessment',
      content: [
        {
          type: 'text',
          content:
            'Analyze performance with four focused report areas, each with period filters and export options.',
        },
        {
          type: 'list',
          title: 'Available reports',
          items: [
            'Sales Reports — KPIs, category breakdown, payment-method split, daily trend and top products.',
            'Inventory Reports — stock status, low-stock items, inventory value and movement.',
            'Customer Reports — segmentation, repeat rate, lifetime value and top customers.',
            'Product Performance — top/bottom performers, category profitability and stock turnover.',
          ],
        },
        {
          type: 'steps',
          title: 'Generate and export',
          items: [
            'Open the report you need from Reports & Analytics in the sidebar.',
            'Choose a period — today, this week, this month, this year, or a custom range.',
            'Read the KPI tiles, charts and tables.',
            'Export to Excel, PDF or CSV.',
          ],
        },
        {
          type: 'tip',
          title: 'Report visibility',
          content:
            'Each report is gated by its own permission (report.sales, report.inventory, report.customers). You will only see the reports your role allows.',
        },
      ],
    },
    {
      id: 'users-roles',
      title: 'Users & Permissions',
      icon: 'admin_panel_settings',
      content: [
        {
          type: 'text',
          content:
            'Control who can do what with user accounts and a visual permission matrix for roles.',
        },
        {
          type: 'steps',
          title: 'Create a user',
          items: [
            'Open User Management from the sidebar and click to add a user.',
            'Enter the first name, last name, email and phone, plus job title and department.',
            'Assign a role, then save.',
          ],
        },
        {
          type: 'steps',
          title: 'Edit roles & permissions',
          items: [
            'Open Roles & Permissions from the sidebar.',
            'Use the permission matrix — roles are rows, permissions are columns grouped by domain.',
            'Tick or untick a cell to grant or revoke that permission for the role, then save.',
          ],
        },
        {
          type: 'list',
          title: 'Built-in roles',
          items: [
            'super_admin — full access; bypasses all permission checks.',
            'admin — administration across the organization.',
            'manager — operations and reporting.',
            'cashier — POS and basic customer functions.',
            'inventory_staff — stock and procurement.',
          ],
        },
        {
          type: 'list',
          title: 'Permission domains',
          items: [
            'sale, product, inventory and customer operations.',
            'report (sales / inventory / customers / financial).',
            'user and role management.',
            'settings, receipt, site and audit access.',
          ],
        },
        {
          type: 'warning',
          title: 'Least privilege',
          content:
            'Grant only the permissions a role needs. The super_admin role bypasses every check, so assign it sparingly.',
        },
      ],
    },
    {
      id: 'settings',
      title: 'System Settings',
      icon: 'settings',
      content: [
        {
          type: 'text',
          content:
            'Configure how the platform behaves for your business from Settings.',
        },
        {
          type: 'list',
          title: 'General settings',
          items: [
            'Business name, support email and support phone.',
            'Default site and timezone.',
            'Currency — LKR, USD, EUR or INR.',
            'Locale — English, Sinhala or Tamil.',
            'Invoice prefix and starting number.',
            'Default tax rate, applied to POS totals.',
          ],
        },
        {
          type: 'steps',
          title: 'Customize receipts',
          items: [
            'Open Receipt Designer from the Administration menu.',
            'Adjust the receipt layout, fields and branding.',
            'Save — printed receipts use your template.',
          ],
        },
        {
          type: 'tip',
          title: 'Set tax before selling',
          content:
            'The default tax rate drives every POS total, so confirm it is correct before processing live sales.',
        },
      ],
    },
    {
      id: 'multi-site',
      title: 'Multi-Site Management',
      icon: 'storefront',
      content: [
        {
          type: 'text',
          content:
            'Run multiple locations from one system, switching context without signing out.',
        },
        {
          type: 'steps',
          title: 'Switch sites',
          items: [
            'Click the site name in the top bar.',
            'Choose the site you want to work in.',
            'The current page reloads in the new site context; all data now reflects that site.',
          ],
        },
        {
          type: 'list',
          title: 'Administration',
          items: [
            'Manage locations under Administration > Sites.',
            'Assign users to sites and set each user’s default site.',
            'The site switcher only appears for users with site access and more than one site.',
            'Tenant management (Administration > Tenants) is reserved for super admins.',
          ],
        },
        {
          type: 'tip',
          title: 'Confirm the site',
          content:
            'Sales, inventory and reports all follow the active site. Double-check it before transacting.',
        },
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: 'notifications',
      content: [
        {
          type: 'text',
          content:
            'The bell icon in the top bar keeps you up to date with activity that needs attention.',
        },
        {
          type: 'list',
          title: 'How notifications work',
          items: [
            'A badge shows the number of unread notifications.',
            'Notifications are grouped by category: stock, supplier, product, system, sales and general.',
            'Each item shows who triggered it, a message and a timestamp, with a relevant action where applicable.',
            'Use "Mark all as read" to clear the unread count, or refresh to pull the latest.',
          ],
        },
        {
          type: 'tip',
          title: 'Act from the panel',
          content:
            'Many notifications link straight to the relevant record — for example a low-stock alert opens the product so you can reorder.',
        },
      ],
    },
    {
      id: 'shortcuts',
      title: 'Keyboard Shortcuts',
      icon: 'keyboard',
      content: [
        {
          type: 'text',
          content:
            'Work faster with keyboard shortcuts. Global shortcuts apply anywhere; the function keys below apply on the POS Checkout screen.',
        },
        {
          type: 'list',
          title: 'Global shortcuts',
          items: [
            'Ctrl + K — open the sidebar search and start typing to navigate.',
            'Esc — close any open panel (search, notifications or shortcuts).',
          ],
        },
        {
          type: 'list',
          title: 'POS Checkout shortcuts',
          items: POS_SHORTCUTS.map(
            (s) => `${s.key} — ${s.action}: ${s.description}`,
          ),
        },
        {
          type: 'tip',
          title: 'Quick reference',
          content:
            'The same shortcut list is available any time from the keyboard icon in the top bar.',
        },
      ],
    },
    {
      id: 'audit-logs',
      title: 'Audit Logs',
      icon: 'history',
      content: [
        {
          type: 'text',
          content:
            'Audit Logs provide a tamper-evident trail of actions taken across the platform.',
        },
        {
          type: 'list',
          title: 'Using audit logs',
          items: [
            'Open Audit Logs from the Administration menu.',
            'Filter by date range, user and action type to find specific events.',
            'Entries are read-only and cannot be edited.',
            'Access requires the audit.read permission.',
          ],
        },
        {
          type: 'tip',
          title: 'Accountability',
          content:
            'Use the audit trail to investigate changes to sales, inventory, settings and user accounts.',
        },
      ],
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: 'help_center',
      content: [
        {
          type: 'text',
          content: 'Common issues and how to resolve them.',
        },
        {
          type: 'list',
          title: 'Common issues',
          items: [
            'A menu or button is missing — your role likely lacks that permission; ask your administrator to update it under Roles & Permissions.',
            'Data looks wrong after switching sites — the page reloads in the new site context; confirm the active site in the top bar.',
            'Stock levels are not changing — check that the product has stock tracking enabled in its product form.',
            'Export or print is not working — allow pop-ups for the app and check your browser’s download/print settings.',
            'Cannot sign in — verify your email and password, check Caps Lock, and confirm your internet connection.',
          ],
        },
        {
          type: 'warning',
          title: 'Still stuck?',
          content:
            'If a problem persists, note what you were doing and contact your administrator or the support channels on the Support page.',
        },
      ],
    },
    {
      id: 'support',
      title: 'Getting Support',
      icon: 'support_agent',
      content: [
        {
          type: 'text',
          content:
            'Help is available when you need it.',
        },
        {
          type: 'list',
          title: 'How to get help',
          items: [
            'Open the Support page from the footer or Help menu for guidance and contact options.',
            'Reach out using the support email and phone configured in Settings for your organization.',
            'Your administrator can adjust permissions, sites and settings on your behalf.',
          ],
        },
        {
          type: 'tip',
          title: 'Keep this guide handy',
          content:
            'Reopen this guide any time from the top bar to refresh on a workflow.',
        },
      ],
    },
  ];

  constructor(public dialogRef: MatDialogRef<UserGuideDialogComponent>) {}

  ngOnInit(): void {
    this.filteredSections = this.sections;
    // Auto-select first section
    if (this.sections.length > 0) {
      this.selectedSection = this.sections[0];
    }
  }

  selectSection(section: GuideSection): void {
    this.selectedSection = section;
  }

  filterSections(): void {
    const query = this.searchQuery.toLowerCase().trim();
    if (!query) {
      this.filteredSections = this.sections;
      return;
    }

    this.filteredSections = this.sections.filter(
      (section) =>
        section.title.toLowerCase().includes(query) ||
        section.content.some(
          (content) =>
            content.title?.toLowerCase().includes(query) ||
            content.content?.toLowerCase().includes(query) ||
            content.items?.some((item) => item.toLowerCase().includes(query)),
        ),
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  openFullGuide(): void {
    // Open the full user guide PDF or external documentation
    window.open('/assets/docs/complete-user-guide.pdf', '_blank');
  }
}
